// @flow

/**
 * Markdown Message Component
 * Renders markdown text with sanitization, error boundary protection,
 * and support for custom tags (like <prompt>)
 */

import React, { useMemo } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import MarkdownErrorBoundary from './MarkdownErrorBoundary';
import { normalizeForGfm } from '../utils/formatters';
import { useCustomTags } from '../context';

const MarkdownContainer = styled.div`
  /* Prevent chart content from holding parent width open when viewport shrinks */
  overflow: hidden;
  min-width: 0;

  & p {
    margin: 0.25rem 0;
  }
  & ul,
  & ol {
    margin: 0.25rem 0;
    padding-left: 1.25rem;
  }
  & li {
    margin: 0.15rem 0;
  }
  & pre,
  & code {
    white-space: pre-wrap;
  }
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    margin: 0.8rem 0 0.25rem;
  }
  & table {
    border-collapse: collapse;
    margin: 0.5rem 0;
    width: 100%;
    overflow-x: auto;
    display: block;
  }
  & th,
  & td {
    border: 1px solid var(--primary, #137cbd);
    padding: 0.5rem 0.75rem;
    text-align: left;
  }
  & th {
    background: var(--primary, #137cbd);
    color: white;
    font-weight: 600;
  }
  & tr:nth-child(even) {
    background: var(--card-background, #f9fafb);
  }
  & blockquote {
    margin: 0.75rem 0;
    margin-left: 0;
    padding-left: 1rem;
    border: none;
    border-left: 2px solid var(--primary, #137cbd);
    font-style: italic;
  }
`;

type Props = {
  text: string,
};

const MarkdownMessage = ({ text }: Props): React$Node => {
  const customTagsContext = useCustomTags();
  const tags = customTagsContext?.tags || [];

  // Build sanitization schema dynamically to allow custom tags
  const sanitizeSchema = useMemo(() => {
    if (tags.length === 0) return defaultSchema;
    
    const customTagNames = tags.map(t => t.tagName);
    const customAttributes = {};
    
    tags.forEach(t => {
      if (t.allowedAttributes && t.allowedAttributes.length > 0) {
        customAttributes[t.tagName] = t.allowedAttributes;
      }
    });

    return {
      ...defaultSchema,
      tagNames: [...(defaultSchema.tagNames || []), ...customTagNames],
      attributes: {
        ...defaultSchema.attributes,
        ...customAttributes,
      },
    };
  }, [tags]);

  // Set of tag names that render as block-level elements
  const blockTagNames = useMemo(
    () => new Set(tags.filter(t => t.block).map(t => t.tagName)),
    [tags],
  );

  // Build components map dynamically to render custom tags
  const components = useMemo(() => {
    if (tags.length === 0) return undefined;

    const map = Object.fromEntries(
      tags.map(t => [t.tagName, t.component])
    );

    // Override <p> to render as <div> when it contains block-level custom tags.
    // ReactMarkdown wraps inline content in <p>, but block children (charts, tables)
    // inside <p> cause DOM nesting violations.
    if (blockTagNames.size > 0) {
      map.p = ({ children, node, ...props }) => {
        const childArray = React.Children.toArray(children);
        const hasBlockChild = childArray.some(
          child => child?.type && blockTagNames.has(child.type.displayName || node?.children?.some(
            c => c.type === 'element' && blockTagNames.has(c.tagName)
          ))
        );
        // Simpler heuristic: check the HAST node's children for block-level custom tags
        const hastHasBlock = node?.children?.some(
          c => c.type === 'element' && blockTagNames.has(c.tagName)
        );
        if (hasBlockChild || hastHasBlock) {
          return <div {...props}>{children}</div>;
        }
        return <p {...props}>{children}</p>;
      };
    }

    return map;
  }, [tags, blockTagNames]);

  const safe = normalizeForGfm(text);

  return (
    <MarkdownErrorBoundary fallbackText={safe}>
      <MarkdownContainer>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
          components={components}
        >
          {safe}
        </ReactMarkdown>
      </MarkdownContainer>
    </MarkdownErrorBoundary>
  );
};

export default MarkdownMessage;
